import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, AlertCircle, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

export function LoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const { organization, fetchOrganization, branding } = useOrganization();
    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedOrg, setSelectedOrg] = useState(null);

    useEffect(() => {
        // Get selected org from localStorage and fetch its full data
        const stored = localStorage.getItem('selectedOrg');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                setSelectedOrg(parsed);
                // Fetch full organization data from backend
                if (parsed.id) {
                    fetchOrganization(parsed.id);
                }
            } catch (e) {
                console.error('Error parsing stored org:', e);
            }
        }
    }, [fetchOrganization]);

    const message = location.state?.message;

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const user = await login(email, password);
            toast.success(`Welcome back, ${user.name}!`);
            
            if (user.role === 'super_admin') {
                navigate('/super-admin');
            } else if (user.role === 'admin') {
                navigate('/admin');
            } else if (user.role === 'manager') {
                navigate('/manager');
            } else {
                // All members (residents) go to resident home
                // Note: Shopper dashboard paused - all users use resident experience
                navigate('/home');
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Login failed. Please check your details.');
        } finally {
            setLoading(false);
        }
    };

    const handleBackToGateway = () => {
        localStorage.removeItem('selectedOrg');
        navigate('/');
    };

    // Convert HSL color string to CSS
    const getColorStyle = (hslString) => {
        if (!hslString) return '#8A9A86';
        return `hsl(${hslString})`;
    };

    const accentColor = getColorStyle(branding?.accent_color || branding?.primary_color);
    const bgColor = getColorStyle(branding?.background_color) || '#F9F8F6';
    const textColor = '#2C302E';

    return (
        <motion.div 
            className="min-h-screen flex flex-col"
            style={{ backgroundColor: bgColor }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            data-testid="login-page"
        >
            {/* Back to Gateway */}
            <div className="p-6">
                <button
                    onClick={handleBackToGateway}
                    className="flex items-center gap-2 text-sm transition-opacity hover:opacity-70"
                    style={{ color: textColor, opacity: 0.6 }}
                    data-testid="back-to-gateway"
                >
                    <ChevronLeft className="w-4 h-4" />
                    Back to Canopy
                </button>
            </div>

            <div className="flex-1 flex items-center justify-center px-6 pb-12">
                <div className="w-full max-w-sm">
                    {/* Organization Header */}
                    <motion.div 
                        className="text-center mb-10"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        {branding?.logo_url ? (
                            <img 
                                src={branding.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}` : branding.logo_url} 
                                alt={organization?.name}
                                className="h-16 mx-auto mb-6 object-contain"
                            />
                        ) : (
                            <h1 
                                className="text-3xl mb-2"
                                style={{ 
                                    fontFamily: branding?.heading_font ? `'${branding.heading_font}', Georgia, serif` : "'Cormorant Garamond', serif",
                                    fontWeight: 400,
                                    color: textColor 
                                }}
                            >
                                {organization?.name || 'Residents Plus'}
                            </h1>
                        )}
                        
                        {selectedOrg?.residence && (
                            <p 
                                className="text-sm tracking-wide uppercase"
                                style={{ color: accentColor }}
                            >
                                {selectedOrg.residence.name}
                            </p>
                        )}

                        <p 
                            className="mt-4 text-sm"
                            style={{ color: textColor, opacity: 0.6 }}
                        >
                            Sign in to your resident portal
                        </p>
                    </motion.div>

                    {/* Message from redirect */}
                    {message && (
                        <motion.div 
                            className="mb-6 p-3 rounded-lg text-sm text-center"
                            style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            {message}
                        </motion.div>
                    )}

                    {/* Error message */}
                    {error && (
                        <motion.div 
                            className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg flex items-center gap-3"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                        >
                            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                            <p className="text-sm text-red-600">{error}</p>
                        </motion.div>
                    )}

                    {/* Login Form */}
                    <motion.form 
                        onSubmit={handleLogin} 
                        className="space-y-5"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <div className="space-y-2">
                            <Label 
                                className="text-xs tracking-wider uppercase"
                                style={{ color: textColor, opacity: 0.6 }}
                            >
                                Email
                            </Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: textColor, opacity: 0.3 }} />
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="your.email@example.com"
                                    className="pl-10 h-12 border-stone-200 focus:border-stone-400"
                                    style={{ backgroundColor: '#FFFFFF' }}
                                    required
                                    data-testid="email-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label 
                                className="text-xs tracking-wider uppercase"
                                style={{ color: textColor, opacity: 0.6 }}
                            >
                                Password
                            </Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: textColor, opacity: 0.3 }} />
                                <Input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="pl-10 h-12 border-stone-200 focus:border-stone-400"
                                    style={{ backgroundColor: '#FFFFFF' }}
                                    required
                                    data-testid="password-input"
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full h-12 text-white font-medium tracking-wide"
                            style={{ backgroundColor: accentColor }}
                            data-testid="login-btn"
                        >
                            {loading ? 'Signing in...' : 'Sign In'}
                        </Button>
                    </motion.form>
                </div>
            </div>
        </motion.div>
    );
}

export default LoginPage;
